import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button, InputNumber, Select, Tag } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash } from "@fortawesome/free-solid-svg-icons"
import { unsafeLabel, unsafeRef, unsafeText } from "../../compiler/names"
import {
  Condition,
  ConditionTerm,
  describeConditionGroups,
  groupTerms,
  isNoneGroup,
  isNumberTerm,
  NumberTerm,
  termPath,
} from "../../model/conditions"
import { RAW_NAMES, ShownNames } from "../../model/shownLabels"
import { Catalogue, CatalogueQuestion, findQuestion, QuestionType } from "../../types/catalogue"

/**
 * A group as the editor shows it: whether it reads "none of", whether it is
 * a number condition (one field and its bounds), and its terms. A number
 * condition holds one number term, its path "" until a field is chosen.
 */
export interface DraftGroup {
  none: boolean
  number: boolean
  terms: ConditionTerm[]
}

export interface ConditionGroupsProps {
  /** Every group holds; a plain group when any of its terms does, a "none of" group when none does. */
  value: Condition
  onChange: (next: Condition) => void
  catalogue: Catalogue
  /** The names the tables show (the spec's shown labels); the raw labels by default. */
  names?: ShownNames
  /**
   * Where the groups as shown, unfinished ones included, are kept while this
   * is not mounted. Given, a group added and not yet filled is there again
   * when it comes back, as long as the stored condition has not changed
   * meanwhile.
   */
  draft?: { current: DraftGroup[] | null }
}

interface ConditionOption {
  /** The answer's codebook path. */
  value: string
  /** The answer as the tables show it. */
  label: string
  /** The answer's own label, as the tooltip. */
  title: string
  /**
   * What a search matches, in lower case: the question and the answer as
   * shown, then as labelled when that differs.
   */
  search: string
  /** What the answer's tag reads once chosen: "Gender: Male", each part as shown. */
  text: string
}

export interface ConditionQuestion {
  label: string
  title: string
  options: ConditionOption[]
}

/** Single and multiple choice questions and grids: each answer is a yes or no for every respondent. */
const CHOICE_TYPES: QuestionType[] = [
  "select-one",
  "range",
  "select-many",
  "grid-one",
  "grid-range",
  "grid-many",
]
const NUMBER_TYPES: QuestionType[] = ["number", "grid-number"]

/** A variable nested in the data is named by its path: shown by its parts. */
const variableWords = (q: CatalogueQuestion, names: ShownNames): string => {
  const shown = names.variable(q.label)
  return shown === q.label ? q.label.split("//").join(": ") : shown
}

/** One instance of a question that has several (a grid row, a loop iteration), as the tables show it. */
const instanceWords = (
  q: CatalogueQuestion,
  i: CatalogueQuestion["instances"][number],
  names: ShownNames
): string => names.instance(q.label, i.id, i.label || i.id)

/** A plain question has one instance, with no id. */
const isPlain = (q: CatalogueQuestion): boolean =>
  q.instances.length === 1 && q.instances[0].id === ""

/**
 * What a condition can name, grouped by question, or by grid row and loop
 * iteration ("Satisfaction - Value"): every answer of each single or multiple
 * choice question in the loaded data, then the same of the variables of the
 * prepared data that are not in the questionnaire, each group named "(from
 * the data)". An answer whose label the engine cannot take in a path is not
 * offered: the condition would reach the script as it is. Questions, rows
 * and answers read as the tables show them (`names`), a variable nested in
 * the data by its parts ("Brand: Bank 1: score"); a search finds the labels
 * too.
 */
export const conditionOptions = (
  catalogue: Catalogue,
  names: ShownNames = RAW_NAMES
): ConditionQuestion[] =>
  [...catalogue.questions, ...(catalogue.dataVariables ?? [])].flatMap((q) => {
    if (!CHOICE_TYPES.includes(q.type)) return []
    const plain = isPlain(q)
    const words = variableWords(q, names)
    return q.instances.flatMap((instance) => {
      if (!instance.inData) return []
      const head = plain ? words : `${words} - ${instanceWords(q, instance, names)}`
      const raw = plain ? q.label : `${q.label} - ${instance.label || instance.id}`
      const options = q.options.flatMap((o): ConditionOption[] => {
        const path = instance.optionPaths[o.label]
        if (!path || unsafeLabel(o.label)) return []
        const option = names.option(q.label, o.label)
        // The path in words, each part as shown. A lone yes or no field of
        // the data is its own answer: its path is its label.
        const text = plain ? names.path(path) : `${head}: ${option}`
        const search = [...new Set([text, `${raw}: ${o.label}`])].join("\n").toLowerCase()
        return [{ value: path, label: option, title: o.label, search, text }]
      })
      const group = q.source === "data" ? `${head} (from the data)` : head
      return options.length > 0 ? [{ label: group, title: group, options }] : []
    })
  })

export interface NumberFieldOption {
  /** The field's codebook path. */
  value: string
  /** The field as the tables show it: "Spend", "Spend grid - Brand A", "Spend x2 (from the data)". */
  label: string
  /** The field's own label. */
  title: string
  /** What a search matches, in lower case: the field as shown, then as labelled when that differs. */
  search: string
}

/**
 * The number fields a number condition can name: every instance of a number
 * question or number grid in the loaded data (each cell of a grid, each
 * iteration of a loop), then the number variables of the prepared data. A
 * path the engine cannot take is not offered.
 */
export const numberFieldOptions = (
  catalogue: Catalogue,
  names: ShownNames = RAW_NAMES
): NumberFieldOption[] =>
  [...catalogue.questions, ...(catalogue.dataVariables ?? [])].flatMap((q) => {
    if (!NUMBER_TYPES.includes(q.type)) return []
    const plain = isPlain(q)
    const words = variableWords(q, names)
    return q.instances.flatMap((i): NumberFieldOption[] => {
      if (!i.inData || unsafeText(i.path) || unsafeRef(i.path)) return []
      const shown = plain ? words : `${words} - ${instanceWords(q, i, names)}`
      const label = q.source === "data" ? `${shown} (from the data)` : shown
      const title = plain ? q.label : `${q.label} - ${i.label || i.id}`
      const search = [...new Set([label, title])].join("\n").toLowerCase()
      return [{ value: i.path, label, title, search }]
    })
  })

/**
 * Whether a condition the list does not offer is known to be wrong. Once the
 * data is loaded it is. Before, nothing is judged (the compiler and the
 * review wait for the data too), except a condition on a question of the
 * questionnaire that has no such answer.
 */
const isWrong = (catalogue: Catalogue, path: string): boolean => {
  if (catalogue.knownPaths !== null) return true
  const q = findQuestion(catalogue, path.split("//")[0])
  return q !== null && !q.instances.some((i) => Object.values(i.optionPaths).includes(path))
}

/**
 * A number term inside the answers' list: a stored group can mix answers and
 * numbers, and the list shows each as a tag. No codebook path starts so.
 */
const NUMBER_KEY = "\u0000number:"
const keyOf = (t: ConditionTerm): string => (isNumberTerm(t) ? `${NUMBER_KEY}${JSON.stringify(t)}` : t)
const termOf = (key: string): ConditionTerm =>
  key.startsWith(NUMBER_KEY) ? (JSON.parse(key.slice(NUMBER_KEY.length)) as NumberTerm) : key

/** A number term with its keys in the stored order: path, min, max. */
const numberTerm = (path: string, min?: number, max?: number): NumberTerm => ({
  path,
  ...(min !== undefined ? { min } : {}),
  ...(max !== undefined ? { max } : {}),
})

/** A finite number from a number field; anything else (empty) is no bound. */
const boundOf = (v: number | string | null): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined

const draftOf = (c: Condition): DraftGroup[] =>
  c.map((g) => {
    const terms = groupTerms(g)
    return { none: isNoneGroup(g), number: terms.length === 1 && isNumberTerm(terms[0]), terms }
  })

/** A group is written once it names something: an answer, or a number field with a bound. */
const isFilled = (g: DraftGroup): boolean => {
  if (!g.number) return g.terms.length > 0
  const t = g.terms[0]
  return (
    t !== undefined &&
    isNumberTerm(t) &&
    t.path !== "" &&
    (t.min !== undefined || t.max !== undefined)
  )
}

const conditionOf = (gs: DraftGroup[]): Condition =>
  gs.filter(isFilled).map((g) => (g.none ? { none: g.terms } : g.terms))

interface Group extends DraftGroup {
  id: number
}

/** The groups with an id each, from `next`, so React keeps each one's row. */
const withIds = (gs: DraftGroup[], next: { current: number }): Group[] =>
  gs.map((g) => ({ ...g, id: next.current++ }))

const MODES = [
  { value: "any", label: "Any of" },
  { value: "none", label: "None of" },
]

/**
 * A condition as groups: the answers chosen in one group read "any of", or
 * "none of" when the group is switched so; a number condition is a number
 * field from one value to another (either may be empty). The groups combine
 * with "and". A new group stays here (and in `draft`) until it names
 * something (`isFilled`): `onChange` only ever gets filled groups. An answer
 * or field known to be wrong (`isWrong`) is shown in red and can be removed.
 */
const ConditionGroups: React.FC<ConditionGroupsProps> = ({
  value,
  onChange,
  catalogue,
  names = RAW_NAMES,
  draft,
}) => {
  const nextId = useRef(0)
  const [groups, setGroups] = useState<Group[]>(() => {
    // The groups last shown, while their filled ones are still what is stored.
    const kept = draft?.current
    const same = kept && JSON.stringify(conditionOf(kept)) === JSON.stringify(value)
    return withIds(same ? kept : draftOf(value), nextId)
  })
  const groupsRef = useRef(groups)
  useEffect(() => {
    if (draft) draft.current = groups.map(({ none, number, terms }) => ({ none, number, terms }))
  }, [draft, groups])
  // What this component last wrote, so its own write is not taken for an
  // outside change when it comes back through the store.
  const valueKey = JSON.stringify(value)
  const written = useRef(valueKey)

  useEffect(() => {
    if (valueKey === written.current) return
    written.current = valueKey
    const next = withIds(draftOf(JSON.parse(valueKey) as Condition), nextId)
    groupsRef.current = next
    setGroups(next)
  }, [valueKey])

  const questions = useMemo(() => conditionOptions(catalogue, names), [catalogue, names])
  const known = useMemo(
    () => new Map(questions.flatMap((q) => q.options.map((o) => [o.value, o.text]))),
    [questions]
  )
  const fields = useMemo(() => numberFieldOptions(catalogue, names), [catalogue, names])

  const edit = (fn: (gs: Group[]) => Group[]): void => {
    const next = fn(groupsRef.current)
    groupsRef.current = next
    setGroups(next)
    const saved = conditionOf(next)
    const text = JSON.stringify(saved)
    if (text === written.current) return
    written.current = text
    onChange(saved)
  }
  const editGroup = (id: number, patch: Partial<DraftGroup>): void =>
    edit((gs) => gs.map((x) => (x.id === id ? { ...x, ...patch } : x)))

  const answers = (g: Group, i: number): React.ReactNode => (
    <Select
      mode="multiple"
      size="small"
      allowClear
      showSearch
      className="ts-conditions__select"
      aria-label={`Condition ${i + 1}`}
      placeholder="Choose answers"
      value={g.terms.map(keyOf)}
      options={questions}
      // Called with the answers only, never with a question's group.
      filterOption={(input, option) =>
        option !== undefined &&
        "search" in option &&
        String(option.search).includes(input.trim().toLowerCase())
      }
      onChange={(keys: string[]) => editGroup(g.id, { terms: keys.map(termOf) })}
      tagRender={({ value: key, closable, onClose }) => {
        const term = termOf(String(key))
        // A number term of a stored group: in words, wrong once the data is
        // loaded without that number field.
        const text = isNumberTerm(term)
          ? describeConditionGroups([[term]], names, catalogue)
          : known.get(term)
        const wrong = isNumberTerm(term)
          ? catalogue.knownPaths !== null && !fields.some((f) => f.value === term.path)
          : !text && isWrong(catalogue, term)
        return (
          <Tag
            closable={closable}
            onClose={onClose}
            // Keeps the list closed when a tag is clicked or removed.
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            className={
              wrong ? "ts-conditions__tag ts-conditions__tag--unknown" : "ts-conditions__tag"
            }
          >
            {text ?? names.path(termPath(term))}
          </Tag>
        )
      }}
    />
  )

  const number = (g: Group, i: number): React.ReactNode => {
    const t = g.terms[0]
    const term = t !== undefined && isNumberTerm(t) ? t : numberTerm("")
    const offered = term.path === "" || fields.some((f) => f.value === term.path)
    // A stored field the list does not offer stays chosen, named in words.
    const options = offered
      ? fields
      : [...fields, { value: term.path, label: names.path(term.path), title: term.path, search: "" }]
    const set = (next: NumberTerm): void => editGroup(g.id, { terms: [next] })
    return (
      <>
        <Select
          size="small"
          showSearch
          className="ts-conditions__select"
          aria-label={`Condition ${i + 1}`}
          placeholder="Choose a number"
          value={term.path === "" ? undefined : term.path}
          options={options}
          status={!offered && catalogue.knownPaths !== null ? "error" : undefined}
          optionFilterProp="search"
          notFoundContent="No number in the loaded data"
          onChange={(path: string) => set(numberTerm(path, term.min, term.max))}
        />
        <InputNumber
          size="small"
          className="ts-conditions__bound"
          aria-label={`Condition ${i + 1} from`}
          placeholder="from"
          value={term.min ?? null}
          onChange={(v) => set(numberTerm(term.path, boundOf(v), term.max))}
        />
        <InputNumber
          size="small"
          className="ts-conditions__bound"
          aria-label={`Condition ${i + 1} to`}
          placeholder="to"
          value={term.max ?? null}
          onChange={(v) => set(numberTerm(term.path, term.min, boundOf(v)))}
        />
      </>
    )
  }

  return (
    <div className="ts-conditions">
      {groups.length === 0 && (
        <p className="ts-hint">No filter: everyone in the base is counted.</p>
      )}
      {groups.map((g, i) => (
        <React.Fragment key={g.id}>
          {i > 0 && (
            <div className="ts-conditions__and" aria-hidden>
              and
            </div>
          )}
          <div className="ts-conditions__group">
            <Select
              size="small"
              variant="borderless"
              className="ts-conditions__any"
              popupMatchSelectWidth={false}
              aria-label={`Condition ${i + 1}: any or none`}
              value={g.none ? "none" : "any"}
              options={MODES}
              onChange={(mode: string) => editGroup(g.id, { none: mode === "none" })}
            />
            {g.number ? number(g, i) : answers(g, i)}
            <Button
              type="text"
              size="small"
              danger
              aria-label={`Remove condition ${i + 1}`}
              icon={<FontAwesomeIcon icon={faTrash} />}
              onClick={() => edit((gs) => gs.filter((x) => x.id !== g.id))}
            />
          </div>
        </React.Fragment>
      ))}
      <div className="ts-actions">
        <Button
          size="small"
          onClick={() =>
            edit((gs) => [...gs, { id: nextId.current++, none: false, number: false, terms: [] }])
          }
        >
          Add condition
        </Button>
        <Button
          size="small"
          onClick={() =>
            edit((gs) => [
              ...gs,
              { id: nextId.current++, none: false, number: true, terms: [numberTerm("")] },
            ])
          }
        >
          Add a number condition
        </Button>
      </div>
    </div>
  )
}

export default ConditionGroups
